


































































  import { computed, defineComponent, onServerPrefetch, PropType, ref, watch } from 'vue'
  import { CMS } from '@one/types'
  import { format, isValid, parseISO } from 'date-fns'
  import useCurrentInstance from '~/utils/useCurrentInstance'
  import StaticPageDto = CMS.Responses.StaticPageDto

  interface NodeListPage {
    id: string
    name: string
    slug: string
  }

  interface NodeListArticle {
    id: string
    url: string
    title: string
    publicationDate: string
    description: string
    imageUrl: string
  }

  export default defineComponent({
    name: 'OneCmsStaticPagesNodeList',
    props: {
      pages: {
        type: Array as PropType<NodeListPage[]>,
        required: true,
      },
    },
    setup(props) {
      const vm = useCurrentInstance()
      const articles = ref<NodeListArticle[]>([])
      const isLoading = ref<boolean>(false)
      const searchQuery = ref<string>('')

      const filteredArticles = computed(() => {
        if (!searchQuery.value) {
          return articles.value
        }
        const query = searchQuery.value.toLowerCase()
        return articles.value.filter(
          article =>
            article.title.toLowerCase().includes(query) ||
            article.description.toLowerCase().includes(query),
        )
      })

      const getCachedStaticPageBySlug = (slug: string): StaticPageDto | null => {
        const cached = vm.$store.state.cms?.staticPages?.[slug]
        return cached || null
      }

      const formatPublicationDate = (publicationDate?: string): string => {
        if (!publicationDate) {
          return ''
        }

        const parsedDate = parseISO(publicationDate)
        if (!isValid(parsedDate)) {
          return publicationDate
        }

        return format(parsedDate, 'dd.MM.yyyy HH:mm')
      }

      const getArticleImageUrl = (staticPage: StaticPageDto): string => {
        return (
          staticPage.miniatureUrl ||
          staticPage.bannerUrl ||
          `${vm.$lib.staticBucket}/pngtree-vector-camera-icon-png-min.jpg`
        )
      }

      const mapStaticPageToArticle = (
        page: NodeListPage,
        staticPage: StaticPageDto,
      ): NodeListArticle => ({
        id: page.id,
        url: `/${staticPage.slug || page.slug}`,
        title: staticPage.name || page.name || staticPage.seo?.title || page.slug,
        publicationDate: formatPublicationDate(staticPage.publishedDate),
        description: staticPage.teaserText || staticPage.seo?.description || '',
        imageUrl: getArticleImageUrl(staticPage),
      })

      const mapPagesFromCache = (): NodeListArticle[] => {
        return props.pages.flatMap((page) => {
          const staticPage = getCachedStaticPageBySlug(page.slug)
          if (!staticPage) {
            return []
          }
          return [mapStaticPageToArticle(page, staticPage)]
        })
      }

      const fetchArticles = async () => {
        if (!props.pages?.length) {
          articles.value = []
          return
        }

        const missingPages = props.pages.filter(page => !getCachedStaticPageBySlug(page.slug))
        if (!missingPages.length) {
          articles.value = mapPagesFromCache()
          return
        }

        isLoading.value = true
        try {
          await Promise.allSettled(
            missingPages.map(page => vm.$store.dispatch('cms/fetchStaticPageBySlug', page.slug)),
          )
          articles.value = mapPagesFromCache()
        } finally {
          isLoading.value = false
        }
      }

      articles.value = mapPagesFromCache()
      onServerPrefetch(fetchArticles)

      watch(
        () => props.pages,
        () => {
          articles.value = mapPagesFromCache()
          fetchArticles()
        },
      )

      return {
        articles,
        filteredArticles,
        isLoading,
        searchQuery,
      }
    },
  })
